import { useState, useEffect, useCallback } from 'react';
import { Brain, Clock, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { api } from '../api/client';

const DECISION_CONFIG = {
  BUY:  { color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/20', icon: TrendingUp  },
  SELL: { color: 'text-red-400',   bg: 'bg-red-400/10',   border: 'border-red-400/20',   icon: TrendingDown },
  HOLD: { color: 'text-slate-400', bg: 'bg-slate-400/10', border: 'border-slate-400/20', icon: Minus        },
};

function confidenceBar(confidence) {
  const pct = parseFloat(confidence) * 100 || 0;
  const color = pct >= 70 ? 'bg-green-400' : pct >= 50 ? 'bg-amber-400' : 'bg-slate-600';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-[#1E1E2E] rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-mono text-slate-400 w-8 text-right">{pct.toFixed(0)}%</span>
    </div>
  );
}

function DecisionRow({ decision, strategyName }) {
  const [expanded, setExpanded] = useState(false);
  const cfg  = DECISION_CONFIG[decision.decision] || DECISION_CONFIG.HOLD;
  const Icon = cfg.icon;

  const ts = new Date(decision.timestamp).toLocaleString('es-MX', {
    timeZone: 'America/Mexico_City',
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

  // Extraer reasoning del rawResponse JSON
  let reasoning = null;
  if (decision.reasoning) {
    try {
      const parsed = JSON.parse(decision.reasoning);
      reasoning = parsed.reasoning || null;
    } catch { /* no es JSON válido */ }
  }

  return (
    <div className={`bg-[#13131A] border rounded transition-all duration-150 ${
      expanded ? 'border-amber-500/20' : 'border-[#1E1E2E]'
    }`}>
      {/* Row principal */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02]"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Decisión */}
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-mono font-bold ${cfg.color} ${cfg.bg} ${cfg.border} w-16 justify-center shrink-0`}>
          <Icon size={11} />
          {decision.decision}
        </div>

        {/* Confianza */}
        <div className="w-24 shrink-0">
          {confidenceBar(decision.confidence)}
        </div>

        {/* Precio */}
        <span className="text-xs font-mono text-slate-300 w-20 shrink-0">
          {decision.price ? `$${parseFloat(decision.price).toFixed(2)}` : '—'}
        </span>

        {/* RSI M5 */}
        <span className="text-xs font-mono text-slate-500 w-16 shrink-0">
          RSI {decision.rsiM5 ? parseFloat(decision.rsiM5).toFixed(1) : '—'}
        </span>

        {/* Estrategia */}
        <span className="text-[10px] font-mono text-amber-500/60 flex-1 truncate">
          {strategyName || `#${decision.strategyId}` || '—'}
        </span>

        {/* Timestamp */}
        <span className="text-[11px] font-mono text-slate-600 shrink-0">
          <Clock size={10} className="inline mr-1" />
          {ts}
        </span>

        {/* Expand */}
        <div className="text-slate-600 shrink-0">
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </div>

      {/* Detalle expandido */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-[#1E1E2E] pt-3 space-y-3">

          {/* Métricas */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Modelo</p>
              <p className="text-xs font-mono text-slate-300">{decision.modelUsed || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Latencia</p>
              <p className="text-xs font-mono text-slate-300">{decision.latencyMs ? `${decision.latencyMs}ms` : '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Tokens in / out</p>
              <p className="text-xs font-mono text-slate-300">
                {decision.tokensInput ?? '—'} / {decision.tokensOutput ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Estrategia</p>
              <p className="text-xs font-mono text-slate-300">{strategyName || `id=${decision.strategyId}` || '—'}</p>
            </div>
          </div>

          {/* Reason code — sin truncar */}
          {decision.reasonCode && (
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Reason</p>
              <p className="text-xs font-mono text-slate-300 bg-[#0A0A0F] rounded p-2 leading-relaxed whitespace-pre-wrap break-words">
                {decision.reasonCode}
              </p>
            </div>
          )}

          {/* Contexto M5 */}
          {decision.m5Context && (
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Contexto M5</p>
              <p className="text-xs font-mono text-slate-400 bg-[#0A0A0F] rounded p-2 leading-relaxed whitespace-pre-wrap break-words">
                {decision.m5Context}
              </p>
            </div>
          )}

          {/* Reasoning Claude */}
          {reasoning && (
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Reasoning Claude</p>
              <p className="text-xs font-mono text-slate-400 bg-[#0A0A0F] rounded p-2 leading-relaxed whitespace-pre-wrap break-words">
                {reasoning}
              </p>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

export default function Decisions() {
  const [decisions,   setDecisions]   = useState([]);
  const [strategies,  setStrategies]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [filterDecision,  setFilterDecision]  = useState('ALL');
  const [filterStrategy,  setFilterStrategy]  = useState('ALL');

  useEffect(() => {
    Promise.all([
      api.getDecisions({ limit: 100 }),
      api.getStrategies(),
    ])
      .then(([dec, strat]) => {
        setDecisions(dec.data);
        setStrategies(strat.data);
      })
      .catch(() => setError('No se pudieron cargar las decisiones'))
      .finally(() => setLoading(false));
  }, []);

  // Mapa id → nombre para lookup rápido
  const strategyMap = Object.fromEntries(strategies.map(s => [String(s.id), s.name]));

  // Aplicar filtros
  const filtered = decisions.filter(d => {
    const matchDecision = filterDecision === 'ALL' || d.decision === filterDecision;
    const matchStrategy = filterStrategy === 'ALL' || String(d.strategyId) === filterStrategy;
    return matchDecision && matchStrategy;
  });

  const buys  = filtered.filter(d => d.decision === 'BUY').length;
  const sells = filtered.filter(d => d.decision === 'SELL').length;
  const holds = filtered.filter(d => d.decision === 'HOLD').length;

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex items-center gap-3 text-amber-500 font-mono text-sm">
        <div className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
        Cargando decisiones...
      </div>
    </div>
  );

  return (
    <div className="p-8 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-100 tracking-tight">Decisiones</h1>
          <p className="text-xs text-slate-500 font-mono mt-1">
            {filtered.length} decisiones · {buys} BUY · {sells} SELL · {holds} HOLD
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-green-400">{buys} BUY</span>
          <span className="text-xs font-mono text-red-400">{sells} SELL</span>
          <span className="text-xs font-mono text-slate-500">{holds} HOLD</span>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-4">

        {/* Filtro por decisión */}
        <div className="flex items-center gap-2">
          <Filter size={11} className="text-slate-600" />
          {['ALL', 'BUY', 'SELL', 'HOLD'].map(f => (
            <button
              key={f}
              onClick={() => setFilterDecision(f)}
              className={`text-[10px] font-mono px-2.5 py-1 rounded border transition-all ${
                filterDecision === f
                  ? f === 'BUY'  ? 'border-green-400/30 bg-green-400/10 text-green-400'
                  : f === 'SELL' ? 'border-red-400/30 bg-red-400/10 text-red-400'
                  : f === 'HOLD' ? 'border-slate-400/30 bg-slate-400/10 text-slate-300'
                  :                'border-amber-500/30 bg-amber-500/10 text-amber-400'
                  : 'border-[#1E1E2E] text-slate-600 hover:text-slate-400'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Filtro por estrategia */}
        {strategies.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-slate-600">Estrategia:</span>
            <select
              value={filterStrategy}
              onChange={e => setFilterStrategy(e.target.value)}
              className="text-[10px] font-mono px-2 py-1 rounded border border-[#1E1E2E] bg-[#0A0A0F] text-slate-300 focus:border-amber-400 focus:outline-none"
            >
              <option value="ALL">Todas</option>
              {strategies.map(s => (
                <option key={s.id} value={String(s.id)}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

      </div>

      {error && (
        <div className="text-red-400 text-xs font-mono border border-red-400/20 bg-red-400/5 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {filtered.length === 0 && !error && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Brain size={32} className="text-slate-700 mx-auto mb-3" />
            <p className="text-slate-600 font-mono text-sm">Sin decisiones para este filtro</p>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="space-y-2">
        {filtered.map(d => (
          <DecisionRow
            key={d.id}
            decision={d}
            strategyName={strategyMap[String(d.strategyId)]}
          />
        ))}
      </div>

    </div>
  );
}
